/* eslint-disable max-lines */
import { Container, Card, CardBody, CardHeader, Row, Col, Table, Spinner, Input } from 'reactstrap';
import { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import CommonHeader from '../../../../components/Headers/CommonHeader';
import FinishedAnnualReviewsHistoryCard from './AnnualReview/FinishedAnnualReviewsHistoryCard';
import FeedbackForm from './Feedback/FeedbackForm';
import OneOnOneForm from './OneOnOneForm';
import ImprovementForm from './ImprovementForm';
import VerbalWarningForm from './VerbalWarningForm';
import DisciplinaryActionForm from './DisciplinaryActionForm';
import UserSelectionModal, { UserWithEmployee } from './UserSelectionModal';
import FeedbackSummaryModal from './Feedback/FeedbackSummaryModal';
import OneOnOneSummaryModal from './OneOnOne/OneOnOneSummaryModal';
import PerformanceImprovementSummaryModal from './Improvement/ImprovementSummaryModal';
import VerbalWarningSummaryModal from './VerbalWarning/VerbalWarningSummaryModal';
import DisciplinaryActionSummaryModal from './DisciplinaryAction/DisciplinaryActionSummaryModal';
import AnnualReviewSummaryModal from './AnnualReview/AnnualReviewSummaryModal';
import PriorDiscussionsModal from './PriorDiscussionsModal';
import PerformanceManagement from '../../../../services/PerformanceManagement';
import DraftsPendingFinalizationCard, { DraftsPendingFinalizationCardRef } from './DraftsPendingFinalizationCard';
import ActionsDropdown from './components/ActionsDropdown';
import { Link } from 'react-router-dom';
import { canCreateAnyPerformanceDocument } from '../../../../helpers/performanceManagementPermissions';
import FilterBar, {
  ActiveFilter,
  FilterFieldDef,
  matchesNumberFilter,
  NumberFilterValue,
} from '../DirectoryList/DirectoryFilters';
import {
  countFiltersOnly,
  extractPerformancePeriod,
  sumPerformanceCounts,
  PERFORMANCE_PERIOD_FILTER_KEY,
} from '../../../../helpers/performanceManagementSummary';

enum FormType {
  None = 'none',
  Improvement = 'improvement',
  Feedback = 'feedback',
  OneOnOne = 'oneOnOne',
  PerformanceImprovement = 'performanceImprovement',
  VerbalWarning = 'verbalWarning',
  DisciplinaryAction = 'disciplinaryAction',
}

const COUNT_FILTER_FIELDS: FilterFieldDef[] = [
  { key: 'feedback_count', label: 'Feedback', type: 'number', icon: 'fa-comment' },
  { key: 'one_on_one_count', label: 'One on One', type: 'number', icon: 'fa-users' },
  { key: 'performance_improvement_count', label: 'Performance Improvement', type: 'number', icon: 'fa-chart-line' },
  { key: 'verbal_warning_count', label: 'Verbal Warning', type: 'number', icon: 'fa-exclamation-triangle' },
  { key: 'disciplinary_action_count', label: 'Disciplinary Action Notice', type: 'number', icon: 'fa-gavel' },
  { key: 'annual_review_count', label: 'Annual Review', type: 'number', icon: 'fa-calendar-check' },
];

const PERIOD_FILTER_FIELD: FilterFieldDef = {
  key: PERFORMANCE_PERIOD_FILTER_KEY,
  label: 'Period',
  type: 'daterange',
  icon: 'fa-calendar',
};

const FILTER_FIELDS: FilterFieldDef[] = [PERIOD_FILTER_FIELD, ...COUNT_FILTER_FIELDS];

const PerformanceManagementPage = () => {
  const [activeForm, setActiveForm] = useState<FormType>(FormType.None);
  const [showUserSelection, setShowUserSelection] = useState(false);
  const [pendingFormType, setPendingFormType] = useState<FormType>(FormType.None);
  const [selectedUsers, setSelectedUsers] = useState<UserWithEmployee[]>([]);
  const [performanceData, setPerformanceData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showOneOnOneModal, setShowOneOnOneModal] = useState(false);
  const [showPerformanceImprovementModal, setShowPerformanceImprovementModal] = useState(false);
  const [showPerformanceVerbalWarningModal, setShowPerformanceVerbalWarningModal] = useState(false);
  const [showPerformanceDisciplinaryActionModal, setShowPerformanceDisciplinaryActionModal] = useState(false);
  const [showAnnualReviewModal, setShowAnnualReviewModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>('');
  const [showPriorDiscussionsModal, setShowPriorDiscussionsModal] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [periodStartDate, setPeriodStartDate] = useState('');
  const [periodEndDate, setPeriodEndDate] = useState('');

  const { token, currentSchool, user }: any = useContext(AuthContext);
  const schoolId = currentSchool.value;
  const draftsPendingFinalizationCardRef = useRef<DraftsPendingFinalizationCardRef>(null);
  const canCreatePerformance = canCreateAnyPerformanceDocument(user);

  const loadPerformanceData = useCallback(async () => {
    if (!schoolId || !token) {
      console.log('Missing schoolId or token:', { schoolId, token: !!token });
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const service = PerformanceManagement(token);
      const response = await service.listBySchool(schoolId, undefined, {
        startDate: periodStartDate,
        endDate: periodEndDate,
      });

      let dataToSet = [];
      if (Array.isArray(response)) {
        dataToSet = response;
      } else if (response?.data && Array.isArray(response.data)) {
        dataToSet = response.data;
      } else if (response && typeof response === 'object') {
        dataToSet = [response];
      }

      setPerformanceData(dataToSet);
    } catch (error: any) {
      console.error('Error details:', error, error?.response?.data);
      toast.error(error?.response?.data?.message || 'Error loading performance data');
      setPerformanceData([]);
    } finally {
      setIsLoading(false);
    }
  }, [schoolId, token, periodStartDate, periodEndDate]);

  useEffect(() => {
    loadPerformanceData();
  }, [loadPerformanceData]);

  const handleFiltersChange = (nextFilters: ActiveFilter[]) => {
    const period = extractPerformancePeriod(nextFilters);
    setActiveFilters(nextFilters);
    setPeriodStartDate(period.startDate);
    setPeriodEndDate(period.endDate);
  };

  const filteredData = useMemo(() => {
    const term = search.trim().toLowerCase();
    const countFilters = countFiltersOnly(activeFilters);
    return performanceData.filter((row: any) => {
      const name = String(row.name || row.employee_name || '').toLowerCase();
      if (term && !name.includes(term)) return false;
      return countFilters.every((f) => matchesNumberFilter(Number(row[f.key]) || 0, f.value as NumberFilterValue));
    });
  }, [performanceData, search, activeFilters]);

  const totals = useMemo(() => sumPerformanceCounts(filteredData), [filteredData]);
  const hasPeriodFilter = Boolean(periodStartDate || periodEndDate);

  const openUserSelection = (formType: FormType) => {
    setPendingFormType(formType);
    setShowUserSelection(true);
  };

  const handleFormSelection = (formType: FormType, userId?: string, userName?: string) => {
    if (userId && userName) {
      // Direct form opening for user-specific dropdowns
      openFormDirectly(formType, userId, userName);
    } else {
      // User selection for main dropdown
      openUserSelection(formType);
    }
  };

  const handleUserSelection = (users: UserWithEmployee[]) => {
    setSelectedUsers(users);
    setActiveForm(pendingFormType);
    setShowUserSelection(false);
  };

  const handleFormSuccess = useCallback(() => {
    loadPerformanceData();
    draftsPendingFinalizationCardRef.current?.reload();
  }, [loadPerformanceData]);

  const closeForm = () => {
    setActiveForm(FormType.None);
    setSelectedUsers([]);
    setPendingFormType(FormType.None);
    loadPerformanceData();
  };

  const closeUserSelection = () => {
    setShowUserSelection(false);
    setPendingFormType(FormType.None);
  };

  const openFeedbackModal = (userId: string, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setShowFeedbackModal(true);
  };

  const closeFeedbackModal = () => {
    setShowFeedbackModal(false);
    setSelectedUserId(null);
    setSelectedUserName('');
  };

  const openOneOnOneModal = (userId: string, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setShowOneOnOneModal(true);
  };

  const closeOneOnOneModal = () => {
    setShowOneOnOneModal(false);
    setSelectedUserId(null);
    setSelectedUserName('');
  };

  const openPerformanceImprovementModal = (userId: string, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setShowPerformanceImprovementModal(true);
  };

  const closePerformanceImprovementModal = () => {
    setShowPerformanceImprovementModal(false);
    setSelectedUserId(null);
    setSelectedUserName('');
  };

  const openPerformanceVerbalWarningModal = (userId: string, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setShowPerformanceVerbalWarningModal(true);
  };

  const closePerformanceVerbalWarningModal = () => {
    setShowPerformanceVerbalWarningModal(false);
    setSelectedUserId(null);
    setSelectedUserName('');
  };

  const openPerformanceDisciplinaryActionModal = (userId: string, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setShowPerformanceDisciplinaryActionModal(true);
  };

  const closePerformanceDisciplinaryActionModal = () => {
    setShowPerformanceDisciplinaryActionModal(false);
    setSelectedUserId(null);
    setSelectedUserName('');
  };

  const openAnnualReviewModal = (userId: string, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setShowAnnualReviewModal(true);
  };

  const closeAnnualReviewModal = () => {
    setShowAnnualReviewModal(false);
    setSelectedUserId(null);
    setSelectedUserName('');
  };

  const closePriorDiscussionsModal = () => {
    setShowPriorDiscussionsModal(false);
    setSelectedUserId(null);
    setSelectedUserName('');
  };

  const openFormDirectly = (formType: FormType, userId: string, userName: string) => {
    // Find the user data from performanceData to get complete information
    const userFromData = performanceData.find((user: any) => String(user.user_id) === userId);

    const userData: UserWithEmployee = {
      id: userId,
      firstName: userName.split(' ')[0] || userName,
      lastName: userName.split(' ').slice(1).join(' ') || '',
      email: userFromData?.email || '',
      password: '',
      status: 'ACTIVE',
      roles: [],
      language: 'en',
      groupId: '',
      birthday: '',
      createdAt: new Date(),
      updatedAt: new Date(),
      picture: '',
      employee: {
        id: userFromData?.employee_id || userId,
        title: userFromData?.position ? { name: userFromData.position } : undefined,
        department: userFromData?.department ? { name: userFromData.department } : undefined,
        supervisor: userFromData?.supervisor ? { name: userFromData.supervisor } : undefined,
        startDate: userFromData?.start_date || '',
      },
    };
    setSelectedUsers([userData]);
    setActiveForm(formType);
    setUserDropdownOpen(null);
  };

  const toggleUserDropdown = (userId: string) => {
    setUserDropdownOpen(userDropdownOpen === userId ? null : userId);
  };

  return (
    <>
      <CommonHeader />
      <Container className="mt--8 pt-3 mb-9" fluid>
        <Row className="justify-content-center mt-2">
          <Col md="8">
            <DraftsPendingFinalizationCard ref={draftsPendingFinalizationCardRef} onModalClose={loadPerformanceData} />
          </Col>
        </Row>
      </Container>
      <Container className="mt--7" fluid>
        <Card className="shadow">
          <CardHeader className="border-0">
            <Row className="align-items-center">
              <Col xs="8">
                <h3 className="mb-0">Performance Management</h3>
              </Col>
              <Col xs="4" className="text-right">
                {canCreatePerformance ? (
                  <ActionsDropdown
                    isOpen={false}
                    onToggle={() => {}}
                    onFormSelect={handleFormSelection}
                    isMainDropdown={true}
                  />
                ) : null}
              </Col>
            </Row>
            <Row className="mt-3">
              <Col xs="12">
                <Input
                  type="text"
                  placeholder="Search by Name"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </Col>
              <Col xs="12" className="mt-3">
                <FilterBar
                  fields={FILTER_FIELDS}
                  filters={activeFilters}
                  onChange={handleFiltersChange}
                  optionsFor={() => []}
                />
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : (
              <>
                {performanceData.length === 0 && (
                  <div className="mb-3">
                    <p className="text-muted mb-0">
                      <i className="fas fa-info-circle mr-2" />
                      {hasPeriodFilter
                        ? 'No records found for the selected period.'
                        : canCreatePerformance
                        ? 'No records found. Click "Create New" to add a performance management document.'
                        : 'No records found.'}
                    </p>
                  </div>
                )}
                <Table className="align-items-center table-flush" responsive>
                  <thead className="thead-light">
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col" className="text-center">
                        Feedback
                      </th>
                      <th scope="col" className="text-center">
                        One on One
                      </th>
                      <th scope="col" className="text-center">
                        Performance Improvement
                      </th>
                      <th scope="col" className="text-center">
                        Verbal Warning
                      </th>
                      <th scope="col" className="text-center">
                        Disciplinary Action Notice
                      </th>
                      <th scope="col" className="text-center">
                        Annual Review
                      </th>
                      {canCreatePerformance ? <th scope="col" className="text-center"></th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length === 0 ? (
                      <tr>
                        <td colSpan={canCreatePerformance ? 8 : 7} className="text-center text-muted">
                          {performanceData.length === 0 ? 'No users available' : 'No results match your search.'}
                        </td>
                      </tr>
                    ) : (
                      <>
                        <tr className="bg-secondary font-weight-bold">
                          <td>Total</td>
                          <td className="text-center">{totals.feedback_count}</td>
                          <td className="text-center">{totals.one_on_one_count}</td>
                          <td className="text-center">{totals.performance_improvement_count}</td>
                          <td className="text-center">{totals.verbal_warning_count}</td>
                          <td className="text-center">{totals.disciplinary_action_count}</td>
                          <td className="text-center">{totals.annual_review_count}</td>
                          {canCreatePerformance ? <td /> : null}
                        </tr>
                        {filteredData.map((row: any) => {
                          const userId = String(row.user_id);
                          const userName = row.name || row.employee_name || 'N/A';
                          return (
                            <tr key={userId}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <Link to={`/users/${userId}/profile/performance`}>
                                    <div className="d-flex align-items-center">
                                      <span className="ml-2">{`${userName}`}</span>
                                    </div>
                                  </Link>
                                </div>
                              </td>

                              <td className="text-center">
                                <span
                                  style={{ cursor: row.feedback_count > 0 ? 'pointer' : 'default' }}
                                  className={row.feedback_count > 0 ? 'text-primary' : ''}
                                  onClick={() => {
                                    if (row.feedback_count > 0) {
                                      openFeedbackModal(userId, userName);
                                    }
                                  }}
                                >
                                  {row.feedback_count || 0}
                                </span>
                              </td>

                              <td className="text-center">
                                <span
                                  style={{ cursor: row.one_on_one_count > 0 ? 'pointer' : 'default' }}
                                  className={row.one_on_one_count > 0 ? 'text-primary' : ''}
                                  onClick={() => {
                                    if (row.one_on_one_count > 0) {
                                      openOneOnOneModal(userId, userName);
                                    }
                                  }}
                                >
                                  {row.one_on_one_count || 0}
                                </span>
                              </td>
                              <td className="text-center">
                                <span
                                  style={{
                                    cursor: row.performance_improvement_count > 0 ? 'pointer' : 'default',
                                  }}
                                  className={row.performance_improvement_count > 0 ? 'text-primary' : ''}
                                  onClick={() => {
                                    if (row.performance_improvement_count > 0) {
                                      openPerformanceImprovementModal(userId, userName);
                                    }
                                  }}
                                >
                                  {row.performance_improvement_count || 0}
                                </span>
                              </td>
                              <td className="text-center">
                                <span
                                  style={{ cursor: row.verbal_warning_count > 0 ? 'pointer' : 'default' }}
                                  className={row.verbal_warning_count > 0 ? 'text-primary' : ''}
                                  onClick={() => {
                                    if (row.verbal_warning_count > 0) {
                                      openPerformanceVerbalWarningModal(userId, userName);
                                    }
                                  }}
                                >
                                  {row.verbal_warning_count || 0}
                                </span>
                              </td>
                              <td className="text-center">
                                <span
                                  style={{
                                    cursor: row.disciplinary_action_count > 0 ? 'pointer' : 'default',
                                  }}
                                  className={row.disciplinary_action_count > 0 ? 'text-primary' : ''}
                                  onClick={() => {
                                    if (row.disciplinary_action_count > 0) {
                                      openPerformanceDisciplinaryActionModal(userId, userName);
                                    }
                                  }}
                                >
                                  {row.disciplinary_action_count || 0}
                                </span>
                              </td>
                              <td className="text-center">
                                <span
                                  style={{ cursor: row.annual_review_count > 0 ? 'pointer' : 'default' }}
                                  className={row.annual_review_count > 0 ? 'text-primary' : ''}
                                  onClick={() => {
                                    if (row.annual_review_count > 0) {
                                      openAnnualReviewModal(userId, userName);
                                    }
                                  }}
                                >
                                  {row.annual_review_count || 0}
                                </span>
                              </td>

                              {canCreatePerformance ? (
                                <td className="text-center">
                                  <ActionsDropdown
                                    userId={userId}
                                    userName={userName}
                                    isOpen={userDropdownOpen === userId}
                                    onToggle={() => toggleUserDropdown(userId)}
                                    onFormSelect={handleFormSelection}
                                  />
                                </td>
                              ) : null}
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </tbody>
                </Table>
              </>
            )}
          </CardBody>
        </Card>
        <FinishedAnnualReviewsHistoryCard />
      </Container>

      <UserSelectionModal isOpen={showUserSelection} toggle={closeUserSelection} onConfirm={handleUserSelection} />

      <FeedbackSummaryModal
        isOpen={showFeedbackModal}
        toggle={closeFeedbackModal}
        userId={selectedUserId}
        userName={selectedUserName}
      />

      <OneOnOneSummaryModal
        isOpen={showOneOnOneModal}
        toggle={closeOneOnOneModal}
        userId={selectedUserId}
        userName={selectedUserName}
      />

      <PerformanceImprovementSummaryModal
        isOpen={showPerformanceImprovementModal}
        toggle={closePerformanceImprovementModal}
        userId={selectedUserId}
        userName={selectedUserName}
      />

      <VerbalWarningSummaryModal
        isOpen={showPerformanceVerbalWarningModal}
        toggle={closePerformanceVerbalWarningModal}
        userId={selectedUserId}
        userName={selectedUserName}
      />

      <DisciplinaryActionSummaryModal
        isOpen={showPerformanceDisciplinaryActionModal}
        toggle={closePerformanceDisciplinaryActionModal}
        userId={selectedUserId}
        userName={selectedUserName}
      />

      <AnnualReviewSummaryModal
        isOpen={showAnnualReviewModal}
        toggle={closeAnnualReviewModal}
        userId={selectedUserId}
        userName={selectedUserName}
      />

      <PriorDiscussionsModal
        isOpen={showPriorDiscussionsModal}
        toggle={closePriorDiscussionsModal}
        userId={selectedUserId}
        userName={selectedUserName}
      />

      <ImprovementForm isOpen={activeForm === FormType.Improvement} toggle={closeForm} />
      <FeedbackForm
        isOpen={activeForm === FormType.Feedback}
        toggle={closeForm}
        selectedUsers={selectedUsers}
        onSuccess={handleFormSuccess}
      />
      <OneOnOneForm
        isOpen={activeForm === FormType.OneOnOne}
        toggle={closeForm}
        selectedUsers={selectedUsers}
        onSuccess={handleFormSuccess}
      />
      <ImprovementForm
        isOpen={activeForm === FormType.PerformanceImprovement}
        toggle={closeForm}
        selectedUsers={selectedUsers}
        onSuccess={handleFormSuccess}
      />
      <VerbalWarningForm
        isOpen={activeForm === FormType.VerbalWarning}
        toggle={closeForm}
        selectedUsers={selectedUsers}
        onSuccess={handleFormSuccess}
      />
      <DisciplinaryActionForm
        isOpen={activeForm === FormType.DisciplinaryAction}
        toggle={closeForm}
        selectedUsers={selectedUsers}
        onSuccess={handleFormSuccess}
      />
    </>
  );
};

export default PerformanceManagementPage;
